import React, { useState, useMemo } from 'react';
import { CATALOG_DATA, CatalogProduct } from '../data/catalog';
import { ProductDrawer } from './ProductDrawer';

const LINE_LABELS: Record<string, string> = {
  fusion_world: 'Fusion World',
  masters: 'Masters',
};

const TYPE_COLORS: Record<string, string> = {
  'Booster Box': '#f97316',
  'Manga Booster': '#8b5cf6',
  'Story Booster': '#06b6d4',
  'Starter Deck': '#22c55e',
  'Special Set': '#eab308',
  'Expert Deck': '#3b82f6',
  Accessory: '#6b7280',
};

function statusBadge(releaseDate: string | null, _releaseDateStatus: string): { label: string; color: string } {
  if (!releaseDate) return { label: 'TBA', color: '#6b7280' };
  const now = new Date();
  const rd = new Date(releaseDate);
  if (rd <= now) return { label: 'Released', color: '#22c55e' };
  const diff = rd.getTime() - now.getTime();
  const days = Math.ceil(diff / 86400000);
  if (days <= 60) return { label: `${days}d`, color: '#f97316' };
  return { label: 'Upcoming', color: '#3b82f6' };
}

export function CatalogPanel() {
  const [lineFilter, setLineFilter] = useState<'all' | 'fusion_world' | 'masters'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'released' | 'upcoming'>('all');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [drawerProduct, setDrawerProduct] = useState<CatalogProduct | null>(null);

  const products = CATALOG_DATA;

  const productTypes = useMemo(() => {
    const types = new Set(products.map((p) => p.productType));
    return ['all', ...Array.from(types)];
  }, []);

  const filtered = useMemo(() => {
    const now = new Date();
    return products.filter((p) => {
      if (lineFilter !== 'all' && p.line !== lineFilter) return false;
      if (typeFilter !== 'all' && p.productType !== typeFilter) return false;
      if (statusFilter === 'released' && (!p.releaseDate || new Date(p.releaseDate) > now)) return false;
      if (statusFilter === 'upcoming' && p.releaseDate && new Date(p.releaseDate) <= now) return false;
      if (query.trim()) {
        const q = query.toLowerCase();
        return (
          p.title.toLowerCase().includes(q) ||
          p.setCode.toLowerCase().includes(q) ||
          p.series.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [lineFilter, typeFilter, statusFilter, query, products]);

  // Group by series
  const grouped = useMemo(() => {
    const map = new Map<string, CatalogProduct[]>();
    for (const p of filtered) {
      const key = `${p.line}::${p.series}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    return map;
  }, [filtered]);

  const fwCount = products.filter((p) => p.line === 'fusion_world').length;
  const mastersCount = products.filter((p) => p.line === 'masters').length;
  const upcomingCount = products.filter((p) => !p.releaseDate || new Date(p.releaseDate) > new Date()).length;

  return (
    <div style={s.container}>
      {/* Stats bar */}
      <div style={s.statsRow}>
        {[
          { label: 'Fusion World sets', value: fwCount, color: '#f97316' },
          { label: 'Masters sets', value: mastersCount, color: '#8b5cf6' },
          { label: 'Upcoming releases', value: upcomingCount, color: '#22c55e' },
          { label: 'Total products', value: products.length, color: '#3b82f6' },
        ].map(({ label, value, color }) => (
          <div key={label} style={s.stat}>
            <span style={{ ...s.statNum, color }}>{value}</span>
            <span style={s.statLabel}>{label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={s.filters}>
        <input
          style={s.searchInput}
          placeholder="Search by name, set code, series..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div style={s.filterRow}>
          <div style={s.filterGroup}>
            <span style={s.filterLabel}>Line</span>
            <div style={s.segmented}>
              {(['all', 'fusion_world', 'masters'] as const).map((v) => (
                <button key={v} style={{ ...s.seg, ...(lineFilter === v ? s.segActive : {}) }}
                  onClick={() => setLineFilter(v)}>
                  {v === 'all' ? 'All' : LINE_LABELS[v]}
                </button>
              ))}
            </div>
          </div>
          <div style={s.filterGroup}>
            <span style={s.filterLabel}>Status</span>
            <div style={s.segmented}>
              {(['all', 'upcoming', 'released'] as const).map((v) => (
                <button key={v} style={{ ...s.seg, ...(statusFilter === v ? s.segActive : {}) }}
                  onClick={() => setStatusFilter(v)}>
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div style={s.filterGroup}>
            <span style={s.filterLabel}>Type</span>
            <select style={s.select} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              {productTypes.map((t) => (
                <option key={t} value={t}>{t === 'all' ? 'All types' : t}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <p style={s.count}>{filtered.length} product{filtered.length !== 1 ? 's' : ''} · {grouped.size} series</p>

      {/* One unified grid; series headers span the full width as separator rows
          so cards in each series stay densely packed underneath without losing
          the grouping signal. */}
      <div style={s.grid}>
        {Array.from(grouped.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .flatMap(([groupKey, items]) => {
            const [line, series] = groupKey.split('::');
            const lineColor = line === 'fusion_world' ? '#f97316' : '#8b5cf6';
            return [
              <div key={`h:${groupKey}`} style={s.groupHeaderRow}>
                <span style={{ ...s.linePill, background: lineColor + '20', color: lineColor }}>
                  {LINE_LABELS[line]}
                </span>
                <span style={s.seriesName}>{series}</span>
                <span style={s.groupCount}>{items.length}</span>
              </div>,
              ...items
                .slice()
                .sort((a, b) => a.setCode.localeCompare(b.setCode))
                .map((p) => {
                  const badge = statusBadge(p.releaseDate, p.releaseDateStatus);
                  const isOpen = expanded === p.id;
                  const typeColor = TYPE_COLORS[p.productType] ?? '#6b7280';
                  return (
                    <div
                      key={p.id}
                      style={{ ...s.card, ...(isOpen ? s.cardOpen : {}) }}
                      onClick={() => setExpanded(isOpen ? null : p.id)}
                    >
                      <div style={s.cardTop}>
                        <code style={{ ...s.setCode, color: typeColor }}>{p.setCode}</code>
                        <span style={{ ...s.statusBadge, color: badge.color, background: badge.color + '20' }}>
                          {badge.label}
                        </span>
                      </div>
                      <div style={s.cardTitle}>{p.title}</div>
                      <div style={{ ...s.typeBadge, background: typeColor + '20', color: typeColor }}>
                        {p.productType}
                      </div>

                      {isOpen && (
                        <div style={s.details}>
                          <div style={s.detailRow}>
                            <span style={s.detailLabel}>Release</span>
                            <span>
                              {p.releaseDate ?? 'TBA'}
                              {p.releaseDateStatus === 'Unconfirmed' && (
                                <span style={{ color: '#eab308', marginLeft: '6px', fontSize: '11px' }}>unconfirmed</span>
                              )}
                            </span>
                          </div>
                          {p.msrpPerPack && (
                            <div style={s.detailRow}>
                              <span style={s.detailLabel}>MSRP/pack</span>
                              <span style={{ color: '#f97316', fontWeight: 700 }}>${p.msrpPerPack} USD</span>
                            </div>
                          )}
                          {p.msrpPerPack && p.packsPerBox > 1 && (
                            <div style={s.detailRow}>
                              <span style={s.detailLabel}>Box size</span>
                              <span>{p.packsPerBox} packs · ~${(p.msrpPerPack * p.packsPerBox).toFixed(2)} USD</span>
                            </div>
                          )}
                          {p.description && (
                            <p style={s.description}>{p.description}</p>
                          )}
                          <div style={s.storeLinks}>
                            <button
                              style={s.preorderNowBtn}
                              onClick={(e) => { e.stopPropagation(); setDrawerProduct(p); }}
                            >
                              View all stores →
                            </button>
                            <a href={`https://www.cardmarket.com/en/DragonBallSuper/Products?searchString=${encodeURIComponent(p.title)}`}
                               target="_blank" rel="noopener noreferrer" style={s.storeLink}>Cardmarket</a>
                            <a href={`https://www.amazon.es/s?k=${encodeURIComponent(`dragon ball ${p.setCode}`)}&i=toys`}
                               target="_blank" rel="noopener noreferrer" style={s.storeLink}>Amazon ES</a>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }),
            ];
          })}
      </div>

      {filtered.length === 0 && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#8a93b0' }}>
          No products match your filters.
        </div>
      )}

      <ProductDrawer product={drawerProduct} onClose={() => setDrawerProduct(null)} />
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', gap: '14px' },
  statsRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  stat: { flex: '1 1 100px', background: '#1a1d27', border: '1px solid #2e3347', borderRadius: '8px', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '100px' },
  statNum: { fontSize: '20px', fontWeight: 800, lineHeight: 1 },
  statLabel: { fontSize: '11px', color: '#8a93b0' },
  filters: { background: '#1a1d27', border: '1px solid #2e3347', borderRadius: '8px', padding: '10px', display: 'flex', flexDirection: 'column', gap: '8px' },
  searchInput: { padding: '8px 12px', background: '#22263a', border: '1px solid #2e3347', borderRadius: '6px', color: '#e8eaf0', fontSize: '13px', outline: 'none' },
  filterRow: { display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
  filterLabel: { fontSize: '10px', fontWeight: 600, color: '#8a93b0', textTransform: 'uppercase', letterSpacing: '0.05em' },
  segmented: { display: 'flex', gap: '4px' },
  seg: { padding: '4px 10px', border: '1px solid #2e3347', borderRadius: '5px', background: '#22263a', color: '#8a93b0', cursor: 'pointer', fontSize: '12px', fontWeight: 500 },
  segActive: { borderColor: '#f97316', background: '#f9731620', color: '#f97316' },
  select: { padding: '4px 10px', background: '#22263a', border: '1px solid #2e3347', borderRadius: '5px', color: '#e8eaf0', fontSize: '12px', cursor: 'pointer' },
  count: { fontSize: '12px', color: '#8a93b0' },
  group: { display: 'flex', flexDirection: 'column', gap: '8px' },
  groupHeader: { display: 'flex', alignItems: 'center', gap: '8px' },
  linePill: { fontSize: '10px', fontWeight: 700, padding: '1px 8px', borderRadius: '8px', letterSpacing: '0.03em' },
  seriesName: { fontSize: '13px', fontWeight: 700, color: '#e8eaf0' },
  groupCount: { background: '#2e3347', color: '#8a93b0', fontSize: '10px', padding: '1px 6px', borderRadius: '8px', fontWeight: 600 },
  // Single grid; series headers below span the full row so cards stay densely
  // packed under each header. 130px min keeps 2 columns at a 360px viewport.
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px' },
  // gridColumn 1/-1 makes the header row span every column of the parent grid.
  groupHeaderRow: {
    gridColumn: '1 / -1',
    display: 'flex', alignItems: 'center', gap: '8px',
    paddingTop: '6px', marginTop: '4px',
    borderTop: '1px solid #2e334780',
  },
  card: { background: '#1a1d27', border: '1px solid #2e3347', borderRadius: '8px', padding: '10px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '6px', transition: 'border-color 0.15s' },
  cardOpen: { borderColor: '#f97316' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  setCode: { fontSize: '12px', fontWeight: 800, fontFamily: 'monospace' },
  statusBadge: { fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '6px' },
  cardTitle: { fontSize: '12px', fontWeight: 600, lineHeight: 1.3 },
  typeBadge: { fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '4px', alignSelf: 'flex-start' },
  details: { borderTop: '1px solid #2e3347', paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' },
  detailRow: { display: 'flex', justifyContent: 'space-between', fontSize: '11px' },
  detailLabel: { color: '#8a93b0' },
  description: { fontSize: '11px', color: '#8a93b0', lineHeight: 1.45, margin: 0 },
  storeLinks: { display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' },
  storeLink: { fontSize: '10px', color: '#3b82f6', textDecoration: 'none', padding: '2px 7px', border: '1px solid #3b82f640', borderRadius: '4px' },
  preorderNowBtn: {
    fontSize: '10px', fontWeight: 700, padding: '3px 9px',
    background: '#f9731620', border: '1px solid #f97316', borderRadius: '4px',
    color: '#f97316', cursor: 'pointer',
  },
};
